// SplitPane + DetailPane — a register's list and the open record SIDE BY SIDE
// at 1024px and up (farm audit X-17, spec W-24, decision D2: "list and detail
// side by side at 1024 on the five heaviest registers, Farm Problems first").
//
// Below 1024 the record keeps opening as a <Modal>. At 1024 a modal covered
// the very list a manager is triaging: open one, close it, find the next one,
// open it. Here the record opens in a pane beside the list, the list stays
// scrollable, and tapping the next card swaps the pane.
//
// How a page uses it:
//
//   <SplitPane detail={row ? <Detail key={id} …/> : null}>   // Detail renders <DetailShell>
//     {filters}{list}
//   </SplitPane>
//
// <DetailShell/> is the one a page uses: it renders a <DetailPane/> at ≥ 1024px
// and a <Modal/> below, from the SAME props (title, onClose, snapshot,
// onSubmit, isPending, labels, footerStart, noFooter, overlay). Always put the
// detail in SplitPane's `detail` slot, whichever shell it gets — a Modal is
// fixed, so where it sits in the tree does not move it on screen, and keeping
// ONE position is what lets a record survive the 1024 line: a tablet rotated
// from landscape (pane) to portrait (Modal) with a half-typed form keeps the
// form. Rendered in two places, the detail unmounted and the typing was gone
// without a prompt (W-24 review).
//
// DetailPane on its own takes the same props. What it does differently from a
// Modal, and why:
//  - No backdrop, no scroll lock, no focus TRAP: the list beside it is the
//    point. Focus MOVES to the pane's heading on open and goes back to the
//    card that opened it on close.
//  - A section landmark named by its heading, a 44px Close, Escape closes —
//    only while focus is inside the pane and no dialog sits over it.
//  - The dirty guard is TWO guards: the X / Cancel / Escape confirm (as in a
//    Modal), and the page-leave registry (usePageDirtyGuard), because the
//    bottom nav, the back arrow and the next card are all still tappable. A
//    page that swaps the record on a card tap must `await confirmLeavePage()`
//    first — the pane cannot see that tap.
//  - Sticky under the page header (--page-header-h, W-18) and never taller
//    than the screen: its body scrolls, its footer stays on screen.
//
// The list half is a size container, so a <RegisterGrid/> in it drops to one
// column while the pane is open and goes back to two when it closes.
import { useEffect, useId, useRef, useState, type ComponentProps, type CSSProperties, type FormEvent, type ReactNode, type RefObject } from 'react'
import clsx from 'clsx'
import { Loader2, X } from 'lucide-react'
import Modal from '@/components/Modal'
import SubmitOverlay from '@/components/SubmitOverlay'
import { imperativeDialogOpen } from '@/components/Dialogs'
import { useDirtyClose } from '@/hooks/useDirtyClose'
import { usePageDirtyGuard } from '@/hooks/usePageDirtyGuard'
import { useDeviceClass } from '@/hooks/useDeviceClass'
import { useMediaQuery } from '@/hooks/useMediaQuery'

/** Tailwind's `lg` — the SplitPane grid below keys on the same line. */
const SPLIT_QUERY = '(min-width: 1024px)'

/** True where a record opens beside the list (≥ 1024px) rather than in a Modal. */
export function useSplitView(): boolean {
  return useMediaQuery(SPLIT_QUERY)
}

export function SplitPane({ detail, children, className }: {
  /** The open record (a <DetailPane/>), or null — the list then takes the full width. */
  detail: ReactNode
  children: ReactNode
  /** Classes for the LIST column (its own vertical rhythm, e.g. `space-y-3`). */
  className?: string
}) {
  return (
    <div className={clsx('lg:grid lg:gap-3 lg:items-start', detail ? 'lg:grid-cols-2' : 'lg:grid-cols-1')}>
      <div className={clsx('min-w-0 [container-type:inline-size]', className)}>{children}</div>
      {detail}
    </div>
  )
}

/** The props a detail component hands its shell — the ones <Modal> and
 *  <DetailPane> share. Type a `Shell` prop as ComponentType<DetailShellProps>.
 *  `size` is the Modal's width below 1024 (`form` for a multi-field record —
 *  spec §2 wants ≥ 560px on a tablet); the pane ignores it, because its width
 *  is the split grid's half. */
export type DetailShellProps = Pick<ComponentProps<typeof Modal>,
  'title' | 'subtitle' | 'onClose' | 'snapshot' | 'onSubmit' | 'isPending' | 'submitLabel'
  | 'submitPendingLabel' | 'cancelLabel' | 'footerStart' | 'noFooter' | 'overlay' | 'size' | 'children'>

/** Is `el` out of sight under a sticky header of `headerPx`, or off-screen? */
export function outOfView(rect: { top: number; bottom: number }, headerPx: number, viewportH: number): boolean {
  return rect.top < headerPx || rect.bottom > viewportH
}

/** Bring the card that opened the pane back into view — the list reflows to
 *  one column when the pane opens, and the card can land a screen away. */
function keepInView(el: HTMLElement | null) {
  if (!el || el === document.body || !el.isConnected) return
  const header = parseFloat(getComputedStyle(el).getPropertyValue('--page-header-h')) || 0
  if (outOfView(el.getBoundingClientRect(), header, window.innerHeight)) el.scrollIntoView({ block: 'center' })
}

/** Focus: to the pane's heading on open, back to the opener on close. The
 *  opener is read in the FIRST render — by the time an effect runs, focus may
 *  already have moved. */
function usePaneFocus(headingRef: RefObject<HTMLHeadingElement>) {
  const [trigger] = useState<HTMLElement | null>(() => document.activeElement as HTMLElement | null)
  useEffect(() => {
    headingRef.current?.focus({ preventScroll: true })
    keepInView(trigger)
    return () => { if (trigger?.isConnected && trigger !== document.body) trigger.focus() }
  }, [trigger, headingRef])
}

/** Escape closes — only while focus is inside the pane (the list beside it is
 *  not ours), and never under an imperative dialog or a <Modal> opened from
 *  the pane, which own their Escape. On `document`, like <Modal>'s. */
function useEscapeWithin(paneRef: RefObject<HTMLElement>, close: () => Promise<void>) {
  const closeRef = useRef(close)
  closeRef.current = close
  useEffect(() => {
    function onKey(e: globalThis.KeyboardEvent) {
      if (e.key !== 'Escape' || imperativeDialogOpen()) return
      const t = e.target as Element | null
      if (!t || !paneRef.current?.contains(t) || t.closest('[role="dialog"]')) return
      e.stopPropagation()
      void closeRef.current()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [paneRef])
}

function PaneFooter({ footerStart, cancelLabel = 'Cancel', onCancel, onSubmit, isPending = false,
  submitLabel = 'Save', submitPendingLabel = 'Saving…' }: DetailShellProps & { onCancel: () => void }) {
  return (
    <div className="px-4 py-3 border-t hairline flex items-center gap-2 shrink-0">
      {footerStart}
      <div className="flex-1" />
      <button type="button" onClick={onCancel} className="btn-ghost">{cancelLabel}</button>
      {onSubmit && (
        <button type="submit" disabled={isPending} className="btn-primary disabled:opacity-50 flex items-center gap-2">
          {isPending && <Loader2 size={14} className="animate-spin" />}
          {isPending ? submitPendingLabel : submitLabel}
        </button>
      )}
    </div>
  )
}

function PaneHeader({ titleId, headingRef, title, subtitle, onClose }: {
  titleId: string; headingRef: RefObject<HTMLHeadingElement>; title: ReactNode; subtitle?: ReactNode; onClose: () => void
}) {
  return (
    <div className="px-4 py-3 border-b hairline flex items-center gap-3 shrink-0">
      <div className="flex-1 min-w-0">
        <h2 id={titleId} ref={headingRef} tabIndex={-1} className="truncate text-base font-bold text-white light:text-fg outline-none">{title}</h2>
        {subtitle && <p className="truncate text-xs text-muted">{subtitle}</p>}
      </div>
      <button type="button" onClick={onClose} aria-label="Close"
        className="h-11 w-11 -mr-2 shrink-0 flex items-center justify-center rounded-lg text-gray-400 hover:text-white light:hover:text-fg hover:bg-surface">
        <X size={18} />
      </button>
    </div>
  )
}

/** Same rule as <Modal>: only a button that declared type="submit" submits. */
function submitHandler(isPending: boolean, onSubmit?: () => void) {
  return (e: FormEvent) => {
    e.preventDefault()
    if (isPending) return
    const submitter = (e.nativeEvent as SubmitEvent).submitter
    if (submitter && submitter.getAttribute('type') !== 'submit') return
    onSubmit?.()
  }
}

// No `overflow-*` class on the pane itself: index.css re-zeroes
// --page-header-h on every overflow utility (it marks a scroller), and the
// pane's own `top` reads that variable — it would pin under the header. The
// body is the scroller; the corners need no clipping.
const SHELL_CLASS = 'sticky mt-3 lg:mt-0 flex flex-col rounded-2xl border hairline-strong surface-sunk'

/** Sticky under the page header. */
const PANE_STYLE: CSSProperties = { top: 'calc(var(--page-header-h, 0px) + 0.75rem)' }

/** Never taller than the screen; on touch the bottom nav (BottomNav is
 *  `coarse:` only) is kept clear of the footer. A CLASS, never an inline
 *  style: the floor's Chrome 86 handsets predate dvh, and an inline dvh value
 *  never reaches PostCSS — this one holds var(), so an old engine kept it and
 *  computed `none`, and the pane had no cap. As a class it is written in vh,
 *  with its dvh form behind @supports (height: 1dvh)
 *  (scripts/lib/viewportUnitFallback.mjs, shape 2). */
const PANE_MAX_H_TOUCH = 'max-h-[calc(100dvh-var(--page-header-h,0px)-4.25rem-env(safe-area-inset-bottom))]'
const PANE_MAX_H_MOUSE = 'max-h-[calc(100dvh-var(--page-header-h,0px)-1.5rem)]'

export function DetailPane(props: DetailShellProps) {
  const { title, subtitle, onClose, snapshot = '', onSubmit, overlay, children } = props
  const titleId = useId()
  const headingRef = useRef<HTMLHeadingElement>(null)
  const paneRef = useRef<HTMLElement>(null)
  const attemptClose = useDirtyClose(snapshot, onClose)
  // Leaving the PAGE with an edited record open (bottom nav, back arrow) asks
  // too — a modal's backdrop blocked those taps; a pane does not.
  usePageDirtyGuard(onSubmit ? snapshot : null)
  usePaneFocus(headingRef)
  useEscapeWithin(paneRef, attemptClose)
  const touch = useDeviceClass() !== 'desktop'
  const close = () => void attemptClose()

  const inner = (
    <>
      <PaneHeader titleId={titleId} headingRef={headingRef} title={title} subtitle={subtitle} onClose={close} />
      <div className="overflow-y-auto overscroll-contain flex-1 min-h-0 p-4 [container-type:inline-size]">{children}</div>
      {!props.noFooter && <PaneFooter {...props} onCancel={close} />}
      {overlay && <SubmitOverlay {...overlay} />}
    </>
  )

  return (
    <section ref={paneRef} aria-labelledby={titleId} data-detail-pane className={clsx(SHELL_CLASS, touch ? PANE_MAX_H_TOUCH : PANE_MAX_H_MOUSE)} style={PANE_STYLE}>
      {onSubmit
        ? <form onSubmit={submitHandler(!!props.isPending, onSubmit)} noValidate className="flex flex-col flex-1 min-h-0">{inner}</form>
        : inner}
    </section>
  )
}

/**
 * The detail's shell, chosen by width: a <DetailPane/> beside the list at
 * ≥ 1024px, a <Modal/> below. The dirty guards live HERE, above the switch.
 * Crossing 1024 (a tablet rotated mid-edit) swaps the shell, and a shell
 * mounts its guard's baseline from the snapshot it first sees — which would
 * be the EDITED form, so Close would then discard without asking. The
 * shell's own guards get a constant snapshot and hand every close to this one.
 */
export function DetailShell(props: DetailShellProps) {
  const split = useSplitView()
  const { snapshot = '', onClose, onSubmit } = props
  const attemptClose = useDirtyClose(snapshot, onClose)
  usePageDirtyGuard(onSubmit ? snapshot : null)
  const shell = { ...props, snapshot: '', onClose: () => void attemptClose() }
  return split ? <DetailPane {...shell} /> : <Modal {...shell} />
}
